import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { MonthGrid } from "../components/kalender/MonthGrid.js";
import { VerhuurtypeKaarten } from "../components/kalender/VerhuurtypeKaarten.js";
import { nl } from "../copy/nl.js";
import { fetchAvailability } from "../lib/api.js";
import { clampOffset, monthStart } from "../lib/calendar.js";
import { formatDateRange } from "../lib/format.js";
import { type Selectie, selectieResultaat, volgendeSelectie } from "../lib/selectie.js";

// Two at a time (user feedback: four was a wall). Arrows page through the
// fetched year.
const MONTHS_VISIBLE = 2;
const MONTHS_FETCHED = 12;

function Legend() {
  const items = [
    // bg-kaart, not transparent: on the papier background a transparent
    // swatch was cream-on-cream and invisible (design review, 2026-08-21).
    { label: nl.kalender.vrij, className: "bg-kaart border-lijn" },
    { label: nl.kalender.bezet, className: "bg-dennen border-dennen" },
    {
      label: nl.kalender.wisseldag,
      className:
        "border-dennen/60 bg-[linear-gradient(135deg,var(--color-dennen)_0_46%,transparent_46%)]",
    },
    { label: nl.kalender.eigenWerking, className: "hatch border-[#b3a887]" },
  ];
  return (
    // One item per line on a phone: the wisseldag explanation wrapped into its
    // neighbour when the items flowed (design review, 2026-08-21).
    <div className="flex flex-col gap-1.5 text-mist text-xs sm:flex-row sm:flex-wrap sm:gap-x-5">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-2">
          <i className={`block h-3.5 w-4 shrink-0 rounded border ${item.className}`} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/**
 * The public landing page: the three renting formulas side by side (they are
 * also the tariff display), then the availability calendar. One building, so
 * no space picker; hovering a day names its status, never the group (NFR-6).
 */
export function KalenderPage() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["availability"],
    queryFn: () => fetchAvailability(),
  });

  const [offset, setOffset] = useState(0);
  const [selectie, setSelectie] = useState<Selectie>({});

  if (isPending) {
    return <p className="py-16 text-center text-mist">{nl.algemeen.laden}</p>;
  }

  // "Vandaag" is the building's today: the API computed it in Europe/Brussels
  // and returns it as the window start — never the browser's UTC clock.
  const todayIso = data?.from;
  const space = data?.spaces[0];
  if (isError || !data || !space || todayIso === undefined) {
    return (
      <div className="py-16 text-center">
        <p className="text-mist">{nl.fouten.kalenderNietGeladen}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 min-h-11 rounded-lg bg-rood px-5 font-semibold text-kaart text-sm"
        >
          {nl.algemeen.opnieuwProberen}
        </button>
      </div>
    );
  }

  const months = Array.from({ length: MONTHS_VISIBLE }, (_unused, index) =>
    monthStart(todayIso, offset + index),
  );
  const lastOffset = MONTHS_FETCHED - MONTHS_VISIBLE;
  const page = (delta: number) =>
    setOffset((current) =>
      clampOffset(current + delta * MONTHS_VISIBLE, MONTHS_VISIBLE, MONTHS_FETCHED),
    );

  // Highlight [van, tot] inclusive while selecting — the departure day is part
  // of the stay the renter is looking at, even though occupancy is half-open.
  const isSelected = (dagIso: string): boolean => {
    if (selectie.van === undefined) return false;
    const tot = selectie.tot ?? selectie.van;
    return dagIso >= selectie.van && dagIso <= tot;
  };

  return (
    <div className={selectie.van !== undefined ? "pb-24" : ""}>
      <h1 className="squiggle font-bold font-display text-3xl tracking-tight">
        {nl.kalender.titel}
      </h1>
      <p className="mt-3 mb-4 max-w-xl text-mist">{nl.kalender.intro}</p>

      {/* On a phone the three formula cards are nearly two screens tall and the
          page is named after what sits below them — offer the shortcut before
          the scroll, not after (design review, 2026-08-21). */}
      <a href="#kalender" className="mb-4 inline-block font-semibold text-rood text-sm sm:hidden">
        {nl.kalender.naarKalender}
      </a>

      <VerhuurtypeKaarten />

      {/* The instruction and the legend BEFORE the first month: on a phone the
          explanation two screens below the first mystery cell explained nothing
          (design review, 2026-08-21). */}
      <section id="kalender" className="mt-8 scroll-mt-4">
        <p className="text-mist text-sm">{nl.kalender.selectie.hint}</p>
        <div className="mt-2.5">
          <Legend />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {months.map((month) => (
            <MonthGrid
              key={month.toString()}
              month={month}
              space={space}
              occupancy={data.occupancy}
              todayIso={todayIso}
              isSelected={isSelected}
              onSelectDay={(dagIso) => setSelectie((huidige) => volgendeSelectie(huidige, dagIso))}
              nav={{
                vorige: () => page(-1),
                volgende: () => page(1),
                vorigeUit: offset === 0,
                volgendeUit: offset >= lastOffset,
              }}
            />
          ))}
        </div>

        {/* Trust line (0.22): Kampas' struck-through days left renters phoning
            to double-check; saying the calendar maintains itself removes that. */}
        <p className="mt-4 text-[11.5px] text-mist">{nl.kalender.autoBijgewerkt}</p>
      </section>

      <SelectieBalk
        selectie={selectie}
        unavailable={space.unavailable}
        onWissen={() => setSelectie({})}
      />
    </div>
  );
}

/**
 * The action bar once a renter starts clicking — FIXED to the bottom of the
 * viewport, because at the bottom of the page it sat off-screen and first-time
 * users never found the reserve button (user feedback 2026-08-19). An arrival
 * alone asks for the departure; a complete, valid selection names its formula
 * and offers the button; an invalid one explains why.
 */
function SelectieBalk({
  selectie,
  unavailable,
  onWissen,
}: {
  selectie: Selectie;
  unavailable: { from: string; to: string }[];
  onWissen: () => void;
}) {
  if (selectie.van === undefined) return null;

  const wissen = (
    <button type="button" onClick={onWissen} className="min-h-11 font-semibold text-mist text-sm">
      {nl.kalender.selectie.wissen}
    </button>
  );

  let inhoud: React.ReactNode;
  if (selectie.tot === undefined) {
    inhoud = (
      <>
        <span className="font-mono text-[13px]">{formatDateRange(selectie.van, selectie.van)}</span>
        <span className="font-semibold text-sm">{nl.kalender.selectie.kiesVertrek}</span>
        {wissen}
      </>
    );
  } else {
    const resultaat = selectieResultaat(selectie.van, selectie.tot, unavailable);
    inhoud = resultaat.ok ? (
      <>
        <span className="font-mono text-[13px]">
          {formatDateRange(resultaat.van, resultaat.tot)}
        </span>
        <span className="font-semibold text-sm">
          {nl.kalender.types[resultaat.verhuurtype].naam}
        </span>
        <Link
          to="/aanvraag"
          // `type` is display-only in the URL (the form re-derives it from the
          // dates); kept so a shared link says what kind of stay it is.
          search={{ type: resultaat.verhuurtype, van: resultaat.van, tot: resultaat.tot }}
          className="inline-flex min-h-11 items-center rounded-xl bg-rood px-6 font-semibold text-kaart text-sm hover:bg-rood-donker"
        >
          {nl.kalender.selectie.reserveer}
        </Link>
        {wissen}
      </>
    ) : (
      <>
        <span className="font-semibold text-rood-donker text-sm">
          {nl.kalender.selectie.fouten[resultaat.reason]}
        </span>
        {wissen}
      </>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-lijn border-t bg-kaart/95 px-4 py-3 shadow-[0_-6px_20px_rgba(35,38,28,0.10)] backdrop-blur">
      <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-5 gap-y-2">
        {inhoud}
      </div>
    </div>
  );
}
